import { useAuth } from "@/_core/hooks/useAuth";
import { BuildVersionBadge } from "@/components/BuildVersionBadge";
import { fetchBuildVersion, type BuildVersionPayload } from "@/lib/buildVersion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  FINANCE_EXPANDED_KEY,
  getFinanceRouteLabel,
  getVisibleFinanceSubItems,
  hasFinanceNavAccess,
  isFinanceRouteActive,
  isFinanceSubItemActive,
} from "@/finance/financeNav";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import {
  canReadDashboardQueues,
  getAllowanceStatusFromSearch,
  getDashboardExpensesQueuePath,
  getDashboardQueueFromSearch,
  getDashboardQueuePath,
  getQueueAccessibleLabel,
  getExpensesTabFromSearch,
  getQueueBadgeState,
} from "@/finance/dashboardQueues";
import {
  BarChart3,
  BarChart2,
  BriefcaseBusiness,
  Building2,
  ContactRound,
  Calendar,
  ChevronRight,
  ClipboardList,
  FileDown,
  FileText,
  Landmark,
  LayoutDashboard,
  LogOut,
  Mail,
  MailCheck,
  MessageSquare,
  Network,
  PackageSearch,
  PanelLeft,
  PieChart,
  Plus,
  ReceiptText,
  Settings2,
  TrendingDown,
  TrendingUp,
  Upload,
  Users,
  Wallet,
  WalletCards,
  Wrench,
} from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { DashboardLayoutSkeleton } from './DashboardLayoutSkeleton';
import { Button } from "./ui/button";
import { LoginContainer } from "./LoginContainer";

const FINANCE_ICON_MAP: Record<string, React.ElementType> = {
  "/accounting": BarChart2,
  "/accounting/income": TrendingUp,
  "/accounting/expenses": TrendingDown,
  "/accounting/treasury": WalletCards,
  "/accounting/projects-margin": PieChart,
  "/accounting/budgets": ClipboardList,
  "/accounting/ledger": Landmark,
  "/accounting/imports": Upload,
  "/accounting/management": ReceiptText,
  "/accounting/settings": Settings2,
};

const mainMenuItems: Array<{ icon: any; label: string; path: string; roles?: string[] }> = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: Wrench, label: "Instalaciones", path: "/installations" },
  { icon: Building2, label: "Clientes", path: "/customers", roles: ["admin", "admin_manager", "project_manager", "sales"] },
  { icon: ContactRound, label: "Proveedores", path: "/accounting/suppliers", roles: ["admin", "admin_manager", "project_manager"] },
  { icon: FileText, label: "Partes Diarios", path: "/daily-reports" },
  { icon: Calendar, label: "Calendario", path: "/calendar" },
  { icon: FileDown, label: "Exportar PDFs", path: "/export-pdfs", roles: ["admin", "project_manager"] },
  { icon: BarChart3, label: "Métricas", path: "/metrics", roles: ["admin", "project_manager"] },
];

const configMenuItems: Array<{ icon: any; label: string; path: string; roles?: string[] }> = [
  { icon: PackageSearch, label: "Catálogo", path: "/product-catalog", roles: ["admin", "admin_manager", "project_manager"] },
  { icon: Mail, label: "Plantillas Email", path: "/email-templates", roles: ["admin"] },
  { icon: MailCheck, label: "Destinatarios Email", path: "/email-recipients", roles: ["admin"] },
  { icon: Users, label: "Usuarios", path: "/users", roles: ["admin", "project_manager"] },
];

const administrationMenuItems: Array<{ icon: any; label: string; path: string; roles?: string[] }> = [
  { icon: Network, label: "Mapa de Datos", path: "/administration/data-map", roles: ["admin", "admin_manager"] },
];

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />
  }

  if (!user) {
    return <LoginContainer />;
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const { state, toggleSidebar, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const financeActive = isFinanceRouteActive(location);
  const dashboardQueue = getDashboardQueueFromSearch(search);
  const canReadQueues = canReadDashboardQueues(user?.role);
  const pendingMaterialsQueueQuery = trpc.materials.getPending.useQuery(undefined, { enabled: canReadQueues });
  const pendingNotesQueueQuery = trpc.notes.pendingForDashboardPage.useQuery(
    { offset: 0, limit: 1, includeItems: false },
    { enabled: canReadQueues },
  );
  const pendingExpensesQueueQuery = trpc.expenses.getPendingInvoicingCount.useQuery(undefined, { enabled: canReadQueues });
  const visibleFinanceItems = getVisibleFinanceSubItems(user?.role || "");
  const commercialActive = location.startsWith("/commercial/");
  const [commercialExpanded, setCommercialExpanded] = useState(() => localStorage.getItem("commercial-nav-expanded") === "true" || commercialActive);
  const handleCommercialToggle = (open: boolean) => { setCommercialExpanded(open); localStorage.setItem("commercial-nav-expanded", String(open)); };
  useEffect(() => { if (commercialActive) { setCommercialExpanded(true); localStorage.setItem("commercial-nav-expanded", "true"); } }, [commercialActive]);
  const [appVersion, setAppVersion] = useState<BuildVersionPayload | null>(null);
  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || "";
    fetchBuildVersion(API_URL).then(setAppVersion).catch(err => console.error("Failed to fetch version", err));
  }, []);

  const [financeExpanded, setFinanceExpanded] = useState(() => {
    const saved = localStorage.getItem(FINANCE_EXPANDED_KEY);
    if (saved !== null) return saved === "true";
    return financeActive;
  });

  useEffect(() => {
    if (financeActive) {
      setFinanceExpanded(true);
      localStorage.setItem(FINANCE_EXPANDED_KEY, "true");
    }
  }, [financeActive]);

  const handleFinanceToggle = (open: boolean) => {
    setFinanceExpanded(open);
    localStorage.setItem(FINANCE_EXPANDED_KEY, open.toString());
  };

  const handleNavigate = (path: string) => {
    setLocation(path);
    if (isMobile) setOpenMobile(false);
  };

  const activeLabel = (location === "/" && dashboardQueue === "materials" ? "Materiales solicitados" :
    location === "/" && dashboardQueue === "notes" ? "Notas pendientes" : null) ??
    (location.startsWith("/commercial/") ? "Comercial" : null) ??
    mainMenuItems.find(item => item.path === location)?.label ??
    configMenuItems.find(item => item.path === location)?.label ??
    administrationMenuItems.find(item => item.path === location)?.label ??
    getFinanceRouteLabel(location) ??
    "Menu";


  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className={`h-[4.5rem] justify-center border-b border-sidebar-border/60 ${isCollapsed ? "p-0" : "p-2"}`}>
            <div className={`flex items-center w-full ${isCollapsed ? "justify-center" : "gap-3"}`}>
              <button
                onClick={toggleSidebar}
                className="size-11 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
                title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
              >
                {isCollapsed ? (
                  <img src="/brand/ods-symbol.svg" alt="" aria-hidden="true" className="size-8" />
                ) : (
                  <PanelLeft className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
              {!isCollapsed ? (
                <img
                  src="/brand/ods-logo.svg"
                  alt="ODS Energy"
                  className="h-11 min-w-0 max-w-[calc(100%-3.75rem)] flex-1 object-contain object-left"
                />
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0 overflow-y-auto overscroll-contain pb-3">
            <SidebarMenu className="px-2 py-1">
              {mainMenuItems
                .filter(item => !item.roles || item.roles.includes(user?.role || ""))
                .map(item => {
                  const isActive = location === item.path;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => handleNavigate(item.path)}
                        tooltip={item.label}
                        className={`h-10 transition-all font-normal`}
                      >
                        <item.icon
                          className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`}
                        />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}

              { ["admin", "admin_manager", "sales", "sales_admin"].includes(user?.role || "") && <SidebarMenuItem><Collapsible open={commercialExpanded} onOpenChange={handleCommercialToggle}><CollapsibleTrigger asChild><SidebarMenuButton isActive={commercialActive} tooltip="Comercial" className="h-10 font-normal"><BriefcaseBusiness className={`h-4 w-4 ${commercialActive ? "text-sidebar-primary" : ""}`}/><span>Comercial</span><ChevronRight className={`ml-auto h-4 w-4 transition-transform ${commercialExpanded ? "rotate-90" : ""}`}/></SidebarMenuButton></CollapsibleTrigger><CollapsibleContent><SidebarMenuSub><SidebarMenuSubItem><SidebarMenuSubButton isActive={location === "/commercial/new"} onClick={() => handleNavigate("/commercial/new")}><Plus className="h-4 w-4"/><span>Nueva solicitud</span></SidebarMenuSubButton></SidebarMenuSubItem><SidebarMenuSubItem><SidebarMenuSubButton isActive={location === "/commercial/requests"} onClick={() => handleNavigate("/commercial/requests")}><FileText className="h-4 w-4"/><span>Mis solicitudes</span></SidebarMenuSubButton></SidebarMenuSubItem><SidebarMenuSubItem><SidebarMenuSubButton isActive={location === "/commercial/clients" || location === "/customers"} onClick={() => handleNavigate(user?.role === "sales" ? "/customers" : "/commercial/clients")}><Building2 className="h-4 w-4"/><span>Mis clientes</span></SidebarMenuSubButton></SidebarMenuSubItem></SidebarMenuSub></CollapsibleContent></Collapsible></SidebarMenuItem> }

              {canReadQueues && (
                <>
                  {!isCollapsed && (
                    <div className="px-2 pb-1.5 pt-3">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pendientes</span>
                    </div>
                  )}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location === "/" && dashboardQueue === "materials"}
                      onClick={() => handleNavigate(getDashboardQueuePath("materials"))}
                      tooltip="Materiales solicitados"
                      aria-label={getQueueAccessibleLabel("Materiales solicitados", "solicitudes", {
                        isLoading: pendingMaterialsQueueQuery.isLoading,
                        isError: pendingMaterialsQueueQuery.isError,
                        count: pendingMaterialsQueueQuery.data?.length,
                      })}
                      className="h-10 transition-all font-normal"
                    >
                      <PackageSearch className="h-4 w-4" />
                      <span className="min-w-0 flex-1 truncate">Materiales solicitados</span>
                      <QueueCountBadge
                        isLoading={pendingMaterialsQueueQuery.isLoading}
                        isError={pendingMaterialsQueueQuery.isError}
                        count={pendingMaterialsQueueQuery.data?.length}
                        label="solicitudes de materiales"
                      />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={location === "/" && dashboardQueue === "notes"}
                      onClick={() => handleNavigate(getDashboardQueuePath("notes"))}
                      tooltip="Notas pendientes"
                      aria-label={getQueueAccessibleLabel("Notas pendientes", "hilos", {
                        isLoading: pendingNotesQueueQuery.isLoading,
                        isError: pendingNotesQueueQuery.isError,
                        count: pendingNotesQueueQuery.data?.total,
                      })}
                      className="h-10 transition-all font-normal"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span className="min-w-0 flex-1 truncate">Notas pendientes</span>
                      <QueueCountBadge
                        isLoading={pendingNotesQueueQuery.isLoading}
                        isError={pendingNotesQueueQuery.isError}
                        count={pendingNotesQueueQuery.data?.total}
                        label="hilos de notas"
                      />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}

              {/* Finanzas expandable section */}
              {hasFinanceNavAccess(user?.role || "") && (
                <SidebarMenuItem>
                  <Collapsible open={financeExpanded} onOpenChange={handleFinanceToggle}>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        isActive={financeActive}
                        tooltip="Finanzas"
                        className="h-10 transition-all font-normal"
                      >
                        <Wallet className={`h-4 w-4 ${financeActive ? "text-sidebar-primary" : ""}`} />
                        <span>Finanzas</span>
                        <ChevronRight
                          className={`ml-auto h-4 w-4 shrink-0 transition-transform duration-200 ${financeExpanded ? "rotate-90" : ""}`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {visibleFinanceItems.map(item => {
                          const isActive = isFinanceSubItemActive(item.path, location);
                          const Icon = FINANCE_ICON_MAP[item.path] ?? FileText;
                          return (
                            <SidebarMenuSubItem key={item.path}>
                              <SidebarMenuSubButton
                                isActive={isActive}
                                onClick={() => handleNavigate(item.path)}
                              >
                                <Icon className="h-4 w-4" />
                                <span>{item.label}</span>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                        {canReadQueues && (
                          <SidebarMenuSubItem>
                            <SidebarMenuSubButton
                              asChild
                              isActive={location === "/accounting/expenses" && getExpensesTabFromSearch(search) === "allowances" && getAllowanceStatusFromSearch(search) === "pending-review"}
                            >
                              <Link
                                href={getDashboardExpensesQueuePath()}
                                aria-label={getQueueAccessibleLabel("Gastos pendientes", "gastos en revisión administrativa", {
                                  isLoading: pendingExpensesQueueQuery.isLoading,
                                  isError: pendingExpensesQueueQuery.isError,
                                  count: pendingExpensesQueueQuery.data,
                                })}
                                onClick={() => isMobile && setOpenMobile(false)}
                              >
                                <TrendingDown className="h-4 w-4" />
                                <span>Gastos pendientes</span>
                                <QueueCountBadge
                                  isLoading={pendingExpensesQueueQuery.isLoading}
                                  isError={pendingExpensesQueueQuery.isError}
                                  count={pendingExpensesQueueQuery.data}
                                  label="gastos pendientes de revisión administrativa"
                                />
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        )}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                </SidebarMenuItem>
              )}
            </SidebarMenu>

            {/* Separator visible only when expanded */}
            {!isCollapsed && (
              <div className="px-3 py-2">
                <div className="h-px bg-border" />
              </div>
            )}

            <SidebarMenu className="px-2 py-1">
              {/* Group label */}
              {!isCollapsed && (
                <div className="px-2 pb-1.5">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Configuración
                  </span>
                </div>
              )}
              {configMenuItems
                .filter(item => !item.roles || item.roles.includes(user?.role || ""))
                .map(item => {
                  const isActive = location === item.path;
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        isActive={isActive}
                        onClick={() => handleNavigate(item.path)}
                        tooltip={item.label}
                        className={`h-10 transition-all font-normal`}
                      >
                        <item.icon
                          className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`}
                        />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
            </SidebarMenu>

            {/* Administración section */}
            {administrationMenuItems.some(item => !item.roles || item.roles.includes(user?.role || "")) && (
              <>
                {!isCollapsed && (
                  <div className="px-3 py-2">
                    <div className="h-px bg-border" />
                  </div>
                )}

                <SidebarMenu className="px-2 py-1">
                  {!isCollapsed && (
                    <div className="px-2 pb-1.5">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Administración
                      </span>
                    </div>
                  )}
                  {administrationMenuItems
                    .filter(item => !item.roles || item.roles.includes(user?.role || ""))
                    .map(item => {
                      const isActive = location === item.path || location.startsWith(`${item.path}/`);
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            isActive={isActive}
                            onClick={() => handleNavigate(item.path)}
                            tooltip={item.label}
                            className={`h-10 transition-all font-normal`}
                          >
                            <item.icon
                              className={`h-4 w-4 ${isActive ? "text-sidebar-primary" : ""}`}
                            />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                </SidebarMenu>
              </>
            )}
          </SidebarContent>

          <SidebarFooter className="shrink-0 border-t p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {appVersion ? (
              <div className="px-1 group-data-[collapsible=icon]:hidden">
                <BuildVersionBadge
                  humanVersion={appVersion.humanVersion}
                  commitSha={appVersion.commitSha}
                  builtAt={appVersion.builtAt}
                  environment={appVersion.environment}
                />
              </div>
            ) : null}
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div >

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <span className="tracking-tight text-foreground">
                    {activeLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <main className="min-w-0 flex-1 p-2 sm:p-4">{children}</main>
      </SidebarInset>
    </>
  );
}

function QueueCountBadge({
  isLoading,
  isError,
  count,
  label,
}: {
  isLoading: boolean;
  isError: boolean;
  count: number | undefined;
  label: string;
}) {
  const state = getQueueBadgeState({ isLoading, isError, count });
  if (state === "empty") return null;
  const text = state === "error" ? "!" : state === "loading" ? "…" : String(count);
  const title = state === "error"
    ? `No se pudo cargar el contador de ${label}`
    : state === "loading"
      ? `Cargando contador de ${label}`
      : `${count} ${label}`;
  return (
    <span
      aria-hidden="true"
      title={title}
      className={`ml-auto inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] leading-none tabular-nums group-data-[collapsible=icon]:hidden ${state === "error" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`}
    >
      {text}
    </span>
  );
}
